/**
 * voiceLedger.js — Un anuncio por alerta, aunque el puesto tenga varias
 * pestañas abiertas.
 *
 * `useVoiceAnnouncements` está montado en el AppShell, así que **cada pestaña
 * de la app locuta por su cuenta**: sus defensas contra repetir un anuncio
 * (el `Set` de ids ya vistos y el cooldown por vehículo+tipo) viven en refs de
 * React y no cruzan de pestaña. Con Monitor en una y Reportes en otra —la forma
 * normal de trabajar en un puesto— el mismo SOS se escuchaba DOS veces, una por
 * pestaña, encimadas y desfasadas por el jitter de la red.
 *
 * La solución es un registro compartido: antes de locutar, la pestaña *reclama*
 * la alerta. La primera que llega gana y las demás se callan.
 *
 * ## Por qué localStorage y no un `BroadcastChannel`
 *
 * Un canal solo avisa a quien está escuchando EN ESE INSTANTE: una pestaña que
 * se abre un segundo después no se entera de lo que ya se dijo, y una recarga
 * de página pierde el registro entero. El `localStorage` es el único estado
 * compartido que sobrevive a las dos cosas, y su API es **síncrona**, que es lo
 * que permite leer-y-escribir sin ventana de carrera dentro de una misma
 * pestaña.
 *
 * ## Por qué además un Web Lock
 *
 * Entre pestañas la carrera sí existe: dos pueden leer el registro antes de que
 * ninguna escriba. `navigator.locks` serializa ese leer-modificar-escribir
 * entre todas las pestañas del origen, que es exactamente para lo que existe.
 * Donde no está (Safari viejo, contexto inseguro) se degrada al camino
 * síncrono: la ventana de carrera vuelve a ser de microsegundos, mucho mejor
 * que no tener nada.
 *
 * ## Reclamar no es haber hablado
 *
 * La reclamación tiene dos etapas. `claimAnnouncement` deja una marca
 * PROVISIONAL que vence en segundos; recién cuando la frase salió de verdad,
 * `confirmAnnouncement` la vuelve firme por el TTL largo, y si no salió,
 * `releaseAnnouncement` la borra. Con una sola etapa, una pestaña que ganaba el
 * turno y después no podía hablar —audio nunca desbloqueado, sintetizador que
 * falla, pestaña que se cierra— se llevaba la alerta y callaba a las demás dos
 * minutos. Para un SOS eso es inaceptable, y contradecía la regla que este
 * mismo módulo declara.
 *
 * Nada de esto puede quedar mudo por un fallo: si el storage está bloqueado
 * (modo privado, cookies de terceros) la reclamación devuelve `true` y se
 * vuelve al comportamiento de antes —hablar— en vez de al silencio.
 */

const STORAGE_KEY = "voice.announced.v1";
const LOCK_NAME = "gps-voice-announce";

/**
 * Las marcas que escribió ESTA pestaña, por clave: `key → ts de la entrada`.
 *
 * Es la noción de dueño que le faltaba al registro. Sin ella, confirmar y
 * soltar operaban sobre la clave a secas, y una pestaña lenta podía:
 *
 *  - BORRAR la marca firme de otra —su reclamación venció, la otra la tomó y ya
 *    estaba hablando— y dejar la alerta libre para que una tercera la repita;
 *  - ASCENDER a firme una reclamación ajena con una confirmación vieja que
 *    venía haciendo fila en el lock, callando dos minutos una frase que se
 *    había cortado a la mitad.
 *
 * Con el `ts` a mano, las dos operaciones se preguntan primero "¿esta marca
 * sigue siendo la mía?" y no tocan nada si la respuesta es no.
 */
const misMarcas = new Map();

/** ¿La entrada que está en el registro es la que escribió esta pestaña? */
function esMia(entries, key) {
  const mio = misMarcas.get(key);
  return mio !== undefined && entryTs(entries[key]) === mio;
}

/** Anota la marca propia, sin dejar crecer el mapa más que el registro. */
function anotarMarca(key, ts) {
  // Se desaloja la MÁS VIEJA (un Map itera en orden de inserción), no el mapa
  // entero: vaciándolo, esta pestaña dejaba de reconocer como suyas todas sus
  // marcas vivas de golpe —la del resumen incluida— y confirmar y soltar se
  // volvían no-ops para ellas.
  while (misMarcas.size >= MAX_ENTRIES) {
    const masVieja = misMarcas.keys().next().value;
    if (masVieja === undefined) break;
    misMarcas.delete(masVieja);
  }
  // Re-insertar la vuelve la más nueva, que es lo que hace útil ese orden.
  misMarcas.delete(key);
  misMarcas.set(key, ts);
}

/** La clave del resumen de ráfaga. Fija: ver `claimSummary`. */
const SUMMARY_KEY = "resumen";

/**
 * Cuánto vale una reclamación CONFIRMADA, o sea una que ya terminó en voz.
 *
 * Tiene que cubrir el desfase entre pestañas —una que estaba congelada en
 * segundo plano puede recibir el mismo WebSocket bastante después— sin llegar a
 * silenciar una alerta legítimamente repetida. El cooldown por vehículo+tipo de
 * `useVoiceAnnouncements` es de 60 s; dos minutos lo cubren con margen.
 */
const CLAIM_TTL_MS = 120_000;

/**
 * Cuánto vale una reclamación TODAVÍA SIN CONFIRMAR.
 *
 * Reclamar es pedir el turno, no haberlo usado: entre la reclamación y la
 * primera palabra la pestaña todavía puede no hablar —el audio nunca se
 * desbloqueó, el sintetizador falla, la pestaña se cierra—. Con una sola
 * duración, esa pestaña se llevaba el SOS a la tumba y bloqueaba a las demás
 * durante dos minutos, que es exactamente lo contrario de lo que este módulo
 * dice defender.
 *
 * Así que la reclamación nace provisional y vence pronto. La confirma
 * `confirmAnnouncement` cuando la frase efectivamente salió, y la suelta
 * `releaseAnnouncement` cuando no. Este plazo es el techo de lo que puede
 * tardar una alerta en recuperarse de una pestaña muda: corto para que se note
 * poco, largo para cubrir el tono más el arranque del sintetizador.
 *
 * Diez segundos y no seis porque el arranque de la voz no es inmediato ni
 * acotado: una pestaña en segundo plano está estrangulada por el navegador, y
 * con la ventana corta su marca vencía ANTES de que llegara a hablar — la que
 * había cedido el turno lo tomaba y las dos terminaban hablando.
 */
export const PROVISIONAL_TTL_MS = 10_000;

/**
 * Tope de entradas del registro. Una ráfaga larga no puede hacer crecer el
 * localStorage sin límite (y su cuota es del origen entero, compartida con las
 * preferencias y el token).
 */
const MAX_ENTRIES = 200;

/**
 * Cuánto espera una pestaña OCULTA antes de reclamar.
 *
 * No es una demora cualquiera: es cómo se elige QUIÉN habla. La pestaña que el
 * usuario está mirando es la que con seguridad tiene el audio desbloqueado —la
 * política de autoplay concede por documento y por gesto— así que se le da
 * ventaja para que gane la reclamación. Si todas están ocultas nadie queda
 * mudo: la demora vence y la primera que despierta habla igual.
 *
 * Un cuarto de segundo no se nota en una locución que dura dos.
 *
 * Ojo con el número: la demora se implementa con `setTimeout` y solo corre en
 * una pestaña OCULTA, que es justo donde el navegador estrangula los timers.
 * Chrome, Firefox y Safari los acotan a ≥1 s en una página escondida, y Chrome
 * los alinea a una vez por minuto cuando además lleva rato sin sonar. O sea
 * que 250 ms es el piso, no el plazo: con el navegador minimizado hace rato, la
 * PRIMERA alerta puede tardar bastante más en salir. Se acepta porque la
 * alternativa —reclamar de una— le saca la ventaja a la pestaña visible, que es
 * la única con el audio desbloqueado seguro; pero si algún día la demora se
 * nota de verdad, el arreglo es elegir al que habla sin pasar por un timer.
 */
export const HIDDEN_CLAIM_DELAY_MS = 250;

function storage() {
  try {
    return typeof window === "undefined" ? null : window.localStorage;
  } catch {
    // Acceder a localStorage LANZA (no devuelve null) con las cookies de sitio
    // bloqueadas. Es la razón por la que esto es una función y no una constante.
    return null;
  }
}

function readLedger() {
  const store = storage();
  if (!store) return null;
  try {
    const parsed = JSON.parse(store.getItem(STORAGE_KEY) ?? "{}");
    // Un array también pasa el `typeof === "object"`, y escribir claves sobre
    // él no rompería pero dejaría basura imposible de podar.
    return parsed && typeof parsed === "object" && !Array.isArray(parsed) ? parsed : {};
  } catch {
    return {};
  }
}

/**
 * Una entrada del registro: cuándo se reclamó y si ya terminó en voz.
 *
 * Se guarda como número (confirmada, formato viejo) o como `[ts, ttl]`, con
 * `ttl` en ms y 0 = el plazo provisional. Un registro escrito por una pestaña
 * con la versión anterior son números sueltos y siguen leyéndose como
 * confirmados, que es lo que eran.
 */
function entryTs(value) {
  return Array.isArray(value) ? value[0] : value;
}

function entryTtl(value) {
  if (!Array.isArray(value)) return CLAIM_TTL_MS;
  // El segundo elemento es el plazo pedido, en ms; 0 significa el provisional
  // de siempre. Así una marca puede durar lo que su caso necesita sin inventar
  // otro almacén: la del resumen de una ráfaga, por ejemplo, que no es una
  // alerta y no le sirve ni el plazo corto ni los dos minutos.
  //
  // El provisional es un PISO, no un default: un plazo pedido más corto es el
  // agujero que ese mínimo existe para tapar. Una frase corta a velocidad
  // normal da ~6 s de `speechClaimMs`, y entre la reclamación y el `onstart`
  // pasan el tono, su pausa y el arranque del sintetizador — con 6 s la marca
  // vencía antes de la primera palabra y otra pestaña arrancaba a hablar
  // encima.
  return Math.max(value[1] > 0 ? value[1] : 0, PROVISIONAL_TTL_MS);
}

/** ¿Esta entrada todavía reserva el turno? */
function vigente(value, ahora) {
  const ts = entryTs(value);
  const edad = ahora - ts;
  // Una marca del FUTURO (el reloj del equipo se corrió hacia atrás) se trata
  // como vencida: equivocarse hablando de más es recuperable, equivocarse
  // callando un SOS no.
  return Number.isFinite(ts) && edad >= 0 && edad < entryTtl(value);
}

/** Saca lo vencido y, si aún sobra, lo más viejo. */
function prune(entries) {
  const ahora = Date.now();
  const vivas = Object.entries(entries).filter(([, value]) => vigente(value, ahora));
  if (vivas.length <= MAX_ENTRIES) return Object.fromEntries(vivas);
  return Object.fromEntries(
    vivas.sort(([, a], [, b]) => entryTs(b) - entryTs(a)).slice(0, MAX_ENTRIES),
  );
}

/**
 * El leer-modificar-escribir, sin protección entre pestañas. Llamarlo siempre
 * adentro del lock cuando haya lock.
 */
function claimSync(key, ttlMs = 0, propioVale = false) {
  const entries = readLedger();
  // Sin storage utilizable no hay registro compartido posible: se habla, que es
  // lo que se hacía antes de que este módulo existiera.
  if (entries === null) return true;

  // Una marca PROPIA todavía viva no bloquea cuando quien llama lo pide: el
  // resumen de ráfaga es de esta pestaña y una ráfaga nueva merece el suyo, así
  // que su propio cooldown no puede callarla (ver `claimSummary`).
  if (vigente(entries[key], Date.now()) && !(propioVale && esMia(entries, key))) {
    return false;
  }

  // Nace PROVISIONAL: pedir el turno no es haberlo usado (ver
  // `PROVISIONAL_TTL_MS`).
  const ts = Date.now();
  entries[key] = [ts, ttlMs];
  try {
    storage().setItem(STORAGE_KEY, JSON.stringify(prune(entries)));
  } catch {
    // Cuota llena: el registro queda como estaba y esta pestaña habla. Peor
    // sería reclamar una alerta que no quedó anotada y que otra pestaña, al no
    // verla, diga igual.
    return true;
  }
  anotarMarca(key, ts);
  return true;
}

/**
 * Reescribe la entrada de una clave sobre un registro YA leído; `null` la borra.
 * Nunca lanza.
 *
 * Toma el registro en vez de releerlo porque quien llama acaba de mirarlo para
 * decidir: releerlo eran dos `JSON.parse` más un `stringify` de hasta 200
 * entradas adentro del lock, en el `onstart` del sintetizador y en `pagehide`,
 * que son los dos momentos con menos presupuesto de todo el módulo.
 */
/** @returns {boolean} si la escritura llegó al storage */
function writeEntry(entries, key, value) {
  if (value === null) delete entries[key];
  else entries[key] = value;

  try {
    storage().setItem(STORAGE_KEY, JSON.stringify(prune(entries)));
    return true;
  } catch {
    /* cuota / storage bloqueado: se queda como estaba */
    return false;
  }
}

/**
 * Serializa un leer-modificar-escribir del registro entre TODAS las pestañas.
 *
 * Vale para cualquier escritura, no solo para la reclamación: confirmar y
 * soltar también leen el registro entero y lo vuelven a serializar, así que sin
 * el lock una de ellas puede pisar la reclamación que otra pestaña acababa de
 * anotar (lost update) y dejar que dos digan la misma alerta — justo lo que el
 * módulo existe para impedir.
 */
async function withLedgerLock(fn) {
  const locks = typeof navigator === "undefined" ? null : navigator.locks;
  if (!locks?.request) return fn();

  // `locks.request` rechaza por dos motivos que hay que distinguir: no se pudo
  // TOMAR el lock, o el callback lanzó adentro. Solo el primero admite
  // reintento — repetir un leer-modificar-escribir que ya corrió lo hace releer
  // un registro donde está la marca que él mismo acaba de dejar, y concluir que
  // perdió su propio turno.
  let corrio = false;
  try {
    return await locks.request(LOCK_NAME, () => {
      corrio = true;
      return fn();
    });
  } catch (err) {
    if (corrio) throw err;
    // Sin lock (contexto no seguro, la pestaña navegando) el registro sigue
    // sirviendo: la ventana de carrera vuelve a ser de microsegundos.
    return fn();
  }
}

/**
 * La clave con la que se reconoce una alerta entre pestañas.
 *
 * El id del backend es único por alerta y es lo que se usa siempre que llegue.
 * Sin id —un emisor viejo, o un mensaje recortado— se cae al trío que
 * identifica el evento: vehículo, tipo e instante. NO se cae a "vehículo+tipo"
 * a secas: eso silenciaría un SOS repetido a propósito.
 *
 * @param {{id?: number|string|null, imei?: string, alert_type?: string, occurred_at?: string}} msg
 * @returns {string|null} null si el mensaje no identifica ninguna alerta
 */
export function announcementKey(msg) {
  if (msg?.id != null) return `a:${msg.id}`;
  const imei = msg?.imei ?? "";
  const tipo = msg?.alert_type ?? "";
  const cuando = msg?.occurred_at ?? "";
  if (!imei || !tipo || !cuando) return null;
  return `e:${imei}:${tipo}:${cuando}`;
}

/**
 * ¿Le toca a ESTA pestaña locutar la alerta?
 *
 * @param {string|null} key — de `announcementKey()`
 * @param {{deferMs?: number, ttlMs?: number}} [options] — `deferMs` es la demora
 *   antes de reclamar, para cederle la ventaja a una pestaña visible (ver
 *   `HIDDEN_CLAIM_DELAY_MS`). `ttlMs` es cuánto tiene que durar la marca
 *   provisional: quien llama sabe cuánto va a hablar y el plazo fijo se le
 *   quedaba corto a una frase larga. 0 = el plazo por defecto.
 * @returns {Promise<boolean>} nunca rechaza
 */
export async function claimAnnouncement(key, { deferMs = 0, ttlMs = 0 } = {}) {
  // Sin clave no hay forma de coordinar: se habla. Un anuncio de más es mejor
  // que uno de menos, que es el criterio de todo este módulo.
  if (!key) return true;

  if (deferMs > 0) {
    await new Promise((resolve) => setTimeout(resolve, deferMs));
  }

  // `true` ante cualquier fallo: mejor una alerta de más que un SOS callado.
  return withLedgerLock(() => claimSync(key, ttlMs)).catch(() => true);
}

/**
 * Cuánto vale la reclamación del RESUMEN de una ráfaga ("y 4 alertas más").
 *
 * Es un cooldown, no un turno: el resumen no se confirma ni se suelta, porque
 * no identifica ninguna alerta en particular. Tiene que cubrir el desfase entre
 * las dos pestañas terminando la MISMA ráfaga —la oculta va unos segundos atrás,
 * por la demora con la que cede la ventaja— sin llegar a comerse el resumen de
 * la ráfaga siguiente, que en un puesto movido viene enseguida.
 */
export const SUMMARY_TTL_MS = 10_000;

/**
 * ¿Le toca a ESTA pestaña decir el resumen de la ráfaga?
 *
 * Va por una clave fija y no por la alerta descartada, y la diferencia importa:
 * dos pestañas de un mismo puesto no descartan las mismas alertas —cada una
 * tiene su propio cooldown por vehículo+tipo y su propia cola— así que
 * nombrando el resumen por lo descartado cada una elegía un nombre distinto,
 * las dos ganaban y las dos resumían, encimadas y con números diferentes.
 *
 * @returns {Promise<boolean>} nunca rechaza
 */
export function claimSummary() {
  // `propioVale`: el cooldown es contra las OTRAS pestañas. Una sola pestaña
  // abierta se callaba su propio resumen siguiente, y esas alertas se quedaban
  // colgadas de una cuenta que después salía mezclada con otra ráfaga.
  return withLedgerLock(() => claimSync(SUMMARY_KEY, SUMMARY_TTL_MS, true)).catch(() => true);
}

/**
 * La voz EMPEZÓ a sonar: la reclamación pasa de provisional a firme.
 *
 * Se llama al arrancar y no al terminar, y la diferencia es la que evita el
 * doble anuncio: una frase larga tarda más que `PROVISIONAL_TTL_MS`, así que
 * confirmando al final la marca vencía con la pestaña todavía hablando y la que
 * había cedido el turno lo reintentaba ENCIMA. Al arrancar, en cambio, ya se
 * sabe que esta pestaña puede hablar, que es lo único que las otras necesitan
 * saber.
 */
export function confirmAnnouncement(key) {
  if (!key) return Promise.resolve();
  // Se llama desde el `onstart` del sintetizador, que no es un contexto donde
  // se pueda esperar nada ni atrapar nada: el rechazo se come acá o se
  // convierte en un unhandled rejection en medio de una alerta.
  return withLedgerLock(() => {
    const entries = readLedger();
    if (entries === null) return;
    // Solo se asciende la marca PROPIA. Entre que `onstart` pide el lock y lo
    // obtiene, esa marca pudo dejar de ser de esta pestaña —el cierre la soltó
    // y otra la tomó, o venció y otra la tomó— y ascender la ajena callaría por
    // dos minutos una alerta que nadie terminó de decir.
    if (!esMia(entries, key)) return;
    const ts = Date.now();
    // Igual que al soltar: la marca propia se actualiza SOLO si la escritura
    // entró. Con la cuota llena quedaba `misMarcas` en el instante nuevo contra
    // un registro que seguía en el viejo, así que `esMia` daba false y la
    // pestaña dejaba de reconocer su propia marca viva — al cerrarse no podía
    // soltarla y la alerta quedaba bloqueada en todo el puesto.
    if (writeEntry(entries, key, ts)) anotarMarca(key, ts);
  }).catch(() => {});
}

/**
 * La pestaña reclamó pero la voz nunca arrancó: suelta el turno.
 *
 * Es la mitad que faltaba. Sin ella, una pestaña con el audio bloqueado se
 * quedaba con el SOS y lo dejaba sin decir en todo el puesto hasta que venciera
 * el TTL largo. Soltarla deja que la reintente la que sí puede hablar (ver el
 * reintento de `useVoiceAnnouncements`).
 *
 * Se decide por el ARRANQUE, nunca por el final: el watchdog de `speakText` no
 * puede distinguir "no habló" de "habló y no reportó el final" —`onend` se
 * pierde con la pestaña en segundo plano, está documentado en `speech.js`— y
 * soltar por esa confusión hace que otra pestaña repita una alerta que el
 * puesto ya escuchó.
 */
export function releaseAnnouncement(key) {
  if (!key) return Promise.resolve();
  // Devuelve promesa y quien la llama la espera: soltar el turno tiene que
  // haber ocurrido antes de que el reintento de la otra pestaña lo busque.
  return withLedgerLock(() => soltarSiEsMia(key)).catch(() => {});
}

/**
 * Suelta el turno YA, sin pasar por el lock.
 *
 * Es el camino de la pestaña que se está cerrando: en `pagehide` no queda vida
 * para esperar una promesa, y `localStorage` es la única API que todavía
 * escribe ahí porque es síncrona. Sin esto, cerrar la pestaña en mitad de una
 * frase dejaba la reclamación firme dos minutos y ninguna otra volvía a decir
 * esa alerta — el caso que `PROVISIONAL_TTL_MS` nombra y que la confirmación al
 * arrancar la frase deja de cubrir.
 *
 * Saltarse el lock tiene un precio que conviene nombrar entero: la escritura
 * serializa el registro COMPLETO, así que si otra pestaña anota una
 * reclamación en esa misma ventana de microsegundos, la que se pierde puede ser
 * la de OTRA alerta, no la que se está soltando — y esa otra la va a repetir
 * una tercera. Se acepta igual porque el final sigue siendo "una alerta se dice
 * de más", que es el criterio de todo este módulo, y porque en `pagehide` no
 * queda vida para esperar el lock: la alternativa es no soltar nada y dejar la
 * alerta muda dos minutos, que es el final que sí importa.
 */
export function releaseAnnouncementNow(key) {
  if (!key) return;
  soltarSiEsMia(key);
}

/**
 * Borra la marca SOLO si sigue siendo de esta pestaña.
 *
 * La condición es la que impide el peor final del registro: una pestaña lenta
 * —su reclamación venció, otra tomó el turno y ya está hablando— borrando la
 * marca firme de la que sí habló y dejando la alerta libre para que una tercera
 * la repita.
 */
function soltarSiEsMia(key) {
  const entries = readLedger();
  if (entries === null) return;
  if (!esMia(entries, key)) return;
  // El olvido solo si el borrado ENTRÓ. `writeEntry` no lanza —se traga la
  // cuota llena y el storage bloqueado—, así que olvidando igual la marca
  // quedaba en el registro sin que esta pestaña la reconociera ya como suya: ni
  // para reintentar el soltado ni para confirmarla, y la alerta bloqueada lo
  // que durara su plazo. Conservándola, el intento siguiente puede soltarla.
  if (writeEntry(entries, key, null)) misMarcas.delete(key);
}

/**
 * Solo para tests: olvida QUIÉN escribió cada marca, dejando el registro igual.
 *
 * Es cómo se simula otra pestaña en un solo módulo: las marcas siguen en el
 * `localStorage` compartido, pero esta pestaña deja de reconocerlas como suyas.
 */
export function __forgetOwnMarks() {
  misMarcas.clear();
}

/** Solo para tests: vacía el registro compartido. */
export function __resetLedger() {
  misMarcas.clear();
  try {
    storage()?.removeItem(STORAGE_KEY);
  } catch {
    /* no-op */
  }
}
